import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, Copy, CheckCircle2, Trash2, Link2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  getOAuthProviderMeta,
  getOAuthRedirectUri,
  parseScopesInput,
  formatScopes,
} from '../../data/oauthProvidersMeta';
import {
  createOAuthState,
  loadOAuthAppDraft,
  saveOAuthAppDraft,
  saveOAuthPending,
} from '../../utils/oauthConnectionFlow';
import {
  canConnectViaPipedream,
  connectViaPipedream,
  pipedreamAppForProvider,
} from '../../utils/pipedreamConnect';

const OAuthConnectPanel = ({
  provider,
  connection,
  apiFetch,
  onDelete,
  onConnected,
  pipedreamStatus,
}) => {
  const meta = getOAuthProviderMeta(provider.id);
  const redirectUri = useMemo(() => getOAuthRedirectUri(), []);
  const platformReady = Boolean(provider.platform_oauth_configured);
  // Provider-level hints from the catalog are authoritative: the row already
  // knows it is Pipedream-managed and which app slug to use, so we don't depend
  // on the (much larger, cache-dependent) status map being complete.
  const providerHintsPipedream = provider.connect_via === 'pipedream'
    || Boolean(provider.pipedream_app);
  const pipedreamApp = provider.pipedream_app
    || pipedreamAppForProvider(provider.id, pipedreamStatus);
  const pipedreamEnabled = Boolean(pipedreamStatus?.enabled)
    && Boolean(pipedreamApp)
    && (providerHintsPipedream || canConnectViaPipedream(provider.id, pipedreamStatus));
  // Catalog-only Pipedream apps have nothing to configure — no BYOC credentials,
  // no authorize_url — so showing the client id/secret form implies setup work
  // that does not exist.
  const pipedreamOnly = pipedreamEnabled
    && Boolean(provider.catalog_only || provider.source === 'pipedream')
    && !(provider.fields || []).length
    && !provider.authorize_url
    && !platformReady;
  const isPipedreamConn = (connection?.metadata?.oauth_mode || '') === 'pipedream';

  const [name, setName] = useState(provider.label);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [shopDomain, setShopDomain] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [scopesText, setScopesText] = useState(
    formatScopes(provider.default_scopes?.length ? provider.default_scopes : meta.defaultScopes),
  );
  const [connecting, setConnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(!pipedreamEnabled && !pipedreamOnly);

  useEffect(() => {
    const draft = loadOAuthAppDraft(provider.id);
    if (draft) {
      if (draft.clientId) setClientId(draft.clientId);
      if (draft.name) setName(draft.name);
      if (draft.scopesText) setScopesText(draft.scopesText);
    } else {
      setName(connection?.name || provider.label);
      setScopesText(formatScopes(provider.default_scopes?.length ? provider.default_scopes : meta.defaultScopes));
    }
    setShowAdvanced(!pipedreamEnabled && !pipedreamOnly);
  }, [provider, connection, meta.defaultScopes, pipedreamEnabled, pipedreamOnly]);

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopied(true);
      toast.success('Redirect URI copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const startPipedreamConnect = async () => {
    setConnecting(true);
    try {
      await connectViaPipedream({
        apiFetch,
        providerId: provider.id,
        environment: pipedreamStatus?.environment || 'development',
        pipedreamStatus,
        app: pipedreamApp,
        onConnectUrl: (_url, hostname) => {
          toast.message(`Opening Connect (${hostname || 'secure window'})`);
        },
        onSuccess: async (account) => {
          const accountId = account?.id || account?.account?.id;
          if (!accountId) {
            throw new Error('Connect did not return an account id');
          }
          await apiFetch('/api/connections/pipedream/complete', {
            method: 'POST',
            body: JSON.stringify({
              account_id: accountId,
              provider: provider.id,
              name: name.trim() || provider.label,
            }),
          });
          toast.success(`${provider.label} connected`);
          if (onConnected) await onConnected();
        },
      });
    } catch (e) {
      toast.error(e?.message || 'Connect failed');
    } finally {
      setConnecting(false);
    }
  };

  const startOAuth = async () => {
    const scopes = parseScopesInput(scopesText);
    if (!scopes.length && !platformReady) {
      toast.error('Add at least one OAuth scope');
      return;
    }

    setConnecting(true);
    try {
      if (platformReady) {
        const { authorize_url: authorizeUrl, state } = await apiFetch(
          `/api/connections/oauth2/${provider.id}/platform/start`,
          {
            method: 'POST',
            body: JSON.stringify({
              redirect_uri: redirectUri,
              name: name.trim() || provider.label,
              scopes,
              shop_domain: shopDomain.trim(),
              subdomain: subdomain.trim(),
            }),
          },
        );
        saveOAuthPending({
          providerId: provider.id,
          clientId: '',
          clientSecret: '',
          name: name.trim() || provider.label,
          scopes,
          state,
          redirectUri,
          mode: 'platform',
        });
        window.location.href = authorizeUrl;
        return;
      }

      if (!clientId.trim()) {
        toast.error('Client ID is required');
        setConnecting(false);
        return;
      }
      if (!clientSecret.trim()) {
        toast.error('Client secret is required');
        setConnecting(false);
        return;
      }

      saveOAuthAppDraft(provider.id, { clientId: clientId.trim(), name: name.trim(), scopesText });
      const state = createOAuthState();

      const { authorize_url: authorizeUrl } = await apiFetch(
        `/api/connections/oauth2/${provider.id}/authorize-url`,
        {
          method: 'POST',
          body: JSON.stringify({
            client_id: clientId.trim(),
            redirect_uri: redirectUri,
            scopes,
            state,
          }),
        },
      );

      saveOAuthPending({
        providerId: provider.id,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        name: name.trim() || provider.label,
        scopes,
        state,
        redirectUri,
        mode: 'byoc',
      });

      window.location.href = authorizeUrl;
    } catch (e) {
      toast.error(e.message || 'Failed to start OAuth flow');
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`oauth-panel-${provider.id}`}>
      {connection && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
          <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium mb-1">
            <CheckCircle2 className="w-4 h-4" /> Connected
            {isPipedreamConn && (
              <span className="text-[10px] font-normal text-emerald-200/70 ml-1">via managed OAuth</span>
            )}
          </div>
          {isPipedreamConn ? (
            <p className="text-[11px] text-emerald-200/70">
              Account {connection.metadata?.pipedream_account_id || connection.credentials?.pipedream_account_id || '••••'}
              {connection.metadata?.pipedream_app ? ` · ${connection.metadata.pipedream_app}` : ''}
            </p>
          ) : (
            <>
              <p className="text-[11px] text-emerald-200/70">
                Scopes: {connection.credentials?.scope || scopesText || 'default'}
              </p>
              <p className="text-[10px] text-zinc-500 mt-1">
                Token ends with {connection.credentials?.access_token?.slice(-4) || '••••'}
              </p>
            </>
          )}
        </div>
      )}

      {pipedreamEnabled && (
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-3">
          <p className="text-[11px] text-violet-100/90">
            Connect with managed OAuth — no client ID required.
            {pipedreamApp ? ` App: ${pipedreamApp}` : ''}
          </p>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Connection name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={provider.label}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
              data-testid="pipedream-connection-name"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {connection && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDelete(connection.id)}
                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                data-testid="oauth-disconnect"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={startPipedreamConnect}
              disabled={connecting}
              className="ml-auto bg-violet-600 hover:bg-violet-500"
              data-testid="pipedream-connect"
            >
              {connecting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Link2 className="w-3.5 h-3.5 mr-1" />
              )}
              {connection ? `Reconnect ${provider.label}` : `Connect ${provider.label}`}
            </Button>
          </div>
          {!pipedreamOnly && (
            <button
              type="button"
              className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="oauth-advanced-toggle"
            >
              {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showAdvanced ? 'Hide' : 'Show'} Advanced / bring your own OAuth
            </button>
          )}
        </div>
      )}

      {!pipedreamOnly && (!pipedreamEnabled || showAdvanced) && (
      <>
      {platformReady && (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 text-[11px] text-emerald-200/90">
          Platform OAuth is configured — click Connect below (no client ID needed).
        </div>
      )}

      {!platformReady && provider.catalog_byoc_oauth && (
        <div className="rounded-xl border border-sky-500/25 bg-sky-500/5 p-4 text-[11px] text-sky-200/90 space-y-1">
          <p>Catalog OAuth — create an app in {provider.label}&apos;s developer portal and paste credentials below.</p>
          {provider.docs_connect && (
            <a
              href={provider.docs_connect}
              target="_blank"
              rel="noreferrer"
              className="text-sky-300 underline text-[10px]"
            >
              Open setup docs
            </a>
          )}
        </div>
      )}

      {!platformReady && (
      <>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
          Redirect URI <span className="text-rose-400">*</span>
        </label>
        <div className="flex gap-2">
          <input
            readOnly
            value={redirectUri}
            className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-300 font-mono"
            data-testid="oauth-redirect-uri"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={copyRedirect}
            className="border-zinc-700 shrink-0"
            data-testid="oauth-copy-redirect"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Paste this exact URL into your OAuth app settings.</p>
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
          Client ID <span className="text-rose-400">*</span>
        </label>
        <input
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="OAuth client ID"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
          data-testid="oauth-client-id"
        />
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
          Client secret <span className="text-rose-400">*</span>
        </label>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder="OAuth client secret"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
          data-testid="oauth-client-secret"
        />
        <p className="text-[10px] text-zinc-600 mt-1">Not stored in the browser — required each time you connect.</p>
      </div>
      </>
      )}

      {!pipedreamEnabled && (
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Connection name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={provider.label}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
          data-testid="oauth-connection-name"
        />
      </div>
      )}

      {provider.requires_shop_domain && (
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Shop domain</label>
          <input
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            placeholder="my-store"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      )}

      {provider.requires_subdomain && (
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Subdomain</label>
          <input
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value)}
            placeholder="yourcompany"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      )}

      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
          Scopes {!platformReady && <span className="text-rose-400">*</span>}
        </label>
        <textarea
          value={scopesText}
          onChange={(e) => setScopesText(e.target.value)}
          rows={3}
          placeholder={meta.scopePlaceholder}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-violet-500"
          data-testid="oauth-scopes"
        />
        <p className="text-[10px] text-zinc-600 mt-1">Space- or comma-separated scopes.</p>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        {connection && !pipedreamEnabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onDelete(connection.id)}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            data-testid="oauth-disconnect"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={startOAuth}
          disabled={connecting}
          className="ml-auto bg-violet-600 hover:bg-violet-500"
          data-testid="oauth-connect"
        >
          {connecting ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Link2 className="w-3.5 h-3.5 mr-1" />
          )}
          {connection ? `Reconnect ${provider.label}` : `Connect ${provider.label}`}
          {pipedreamEnabled ? ' (own OAuth)' : ''}
        </Button>
      </div>
      </>
      )}

      {onConnected && connection && (
        <p className="text-[10px] text-zinc-600">
          Reconnect to refresh tokens or update scopes. Disconnect first if switching OAuth apps.
        </p>
      )}
    </div>
  );
};

export default OAuthConnectPanel;
